import { useGateValue } from "@statsig/react-bindings";
import { createIntl } from "react-intl";
import { data, redirect, useLoaderData, useLocation, useNavigate } from "react-router";

import { LandingPage } from "@/components/landing";
import { CarpetLiteOrganicExperience } from "@/components/landing/CarpetLiteOrganicExperience";
import { adaptLandingPageFromContentful } from "@/components/landing/contentfulAdapter";
import NotFound from "@/components/pages/not-found";
import { useGlobalContext } from "@/hooks/useGlobalContext";
import { CARPET_LITE_ORGANIC_EXPERIENCE_GATE } from "@/lib/carpetLiteCampaign";
import {
  DEFAULT_LOCALE,
  getLocaleFromLandingPathname,
  getLocalizedLandingPath,
  importLocaleJson,
  LOCALE_SEARCH_PARAM,
  sanitizeLocale,
} from "@/locale";
import { getLocaleForLandingRequest } from "@/locale.server";
import { messages } from "@/localization/Landing.messages";
import { fetchMarketingLandingPageContentfulData } from "@/server/contentfulMarketingLanding.server";
import { getStatsigClient } from "@/server/statsig.server";

import type { SofaAppLoadContext } from "express-server/main";
import type { AdsLandingPageExperimentParameters } from "@/components/landing/types";
import type { Route } from "./+types/landing";

const ADS_ORIGIN = "https://ads.openai.com";
const LANDING_PAGE_HERO_LAYER = "bazaar_tapestry_landing_page_hero";
const DEFAULT_DESCRIPTION =
  "Reach people as they explore options, compare choices, and make decisions in ChatGPT, with relevant ads that fit naturally into the experience.";
const DEFAULT_TITLE = "Advertise in ChatGPT | OpenAI Ads";

export function meta({ data }: Route.MetaArgs): Route.MetaDescriptors {
  if (data?.notFound) {
    return [{ title: "OpenAI Ads Manager" }, { name: "robots", content: "noindex,nofollow" }];
  }

  const canonicalUrl = data?.meta.canonicalUrl ?? `${ADS_ORIGIN}/`;
  const description = data?.meta.description ?? DEFAULT_DESCRIPTION;
  const ogTitle = data?.meta.title ?? DEFAULT_TITLE;

  return [
    { tagName: "link", rel: "canonical", href: canonicalUrl },
    { title: ogTitle },
    { name: "description", content: description },
    { property: "og:site_name", content: "OpenAI Ads" },
    { property: "og:type", content: "website" },
    { property: "og:title", content: ogTitle },
    { property: "og:description", content: description },
    { property: "og:url", content: canonicalUrl },
    // TODO: Add launch-ready Open Graph and Twitter images once marketing provides them.
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:title", content: ogTitle },
    { name: "twitter:description", content: description },
    { name: "robots", content: "index,follow" },
  ];
}

export async function loader({
  request,
  context,
}: Route.LoaderArgs & { context: SofaAppLoadContext }) {
  const url = new URL(request.url);
  const pathLocale = getLocaleFromLandingPathname(url.pathname);
  const isRootPath = url.pathname === "/";

  if (!isRootPath && pathLocale == null) {
    return data(
      {
        notFound: true as const,
        landingPage: null,
        meta: {
          canonicalUrl: `${ADS_ORIGIN}${url.pathname}`,
          description: DEFAULT_DESCRIPTION,
          title: "OpenAI Ads Manager",
        },
      },
      { status: 404 },
    );
  }

  const queryLocale = sanitizeLocale(url.searchParams.get(LOCALE_SEARCH_PARAM) ?? "");
  if (isRootPath && queryLocale != null) {
    url.pathname = getLocalizedLandingPath(queryLocale);
    url.searchParams.delete(LOCALE_SEARCH_PARAM);
    return redirect(`${url.pathname}${url.search}${url.hash}`, 301);
  }

  if (pathLocale != null) {
    const canonicalPath = getLocalizedLandingPath(pathLocale);
    if (url.pathname !== canonicalPath || url.searchParams.has(LOCALE_SEARCH_PARAM)) {
      url.pathname = canonicalPath;
      url.searchParams.delete(LOCALE_SEARCH_PARAM);
      return redirect(`${url.pathname}${url.search}${url.hash}`, 301);
    }
  }

  const locale = getLocaleForLandingRequest(request);
  const isOrganicCampaignPreviewEnabled =
    context.user != null &&
    context.user.adAccounts.length === 0 &&
    context.adAccountId == null &&
    getStatsigClient().checkGate(context.statsigUser, CARPET_LITE_ORGANIC_EXPERIENCE_GATE, {
      disableExposureLogging: true,
    });
  const [contentfulEntry, localeMessages] = await Promise.all([
    isOrganicCampaignPreviewEnabled ?
      Promise.resolve(null)
    : fetchMarketingLandingPageContentfulData(locale),
    importLocaleJson(locale),
  ]);
  const landingPage = adaptLandingPageFromContentful(contentfulEntry);

  if (!isOrganicCampaignPreviewEnabled && !landingPage.hero) {
    return redirect("/auth/login");
  }

  const intl = createIntl({ locale, defaultLocale: DEFAULT_LOCALE, messages: localeMessages });
  const defaultExperimentParameters: AdsLandingPageExperimentParameters = {
    heroCtaText: landingPage.hero?.ctas[0]?.label ?? "",
    heroHeaderText: landingPage.hero?.headline ?? "",
    heroSubheaderText: landingPage.hero?.subhead ?? "",
    headerCtaText: intl.formatMessage(messages.signUp),
    heroAlignmentClass: "lg:items-end",
  };
  const experimentParameters =
    isOrganicCampaignPreviewEnabled || locale !== DEFAULT_LOCALE ?
      defaultExperimentParameters
    : getLandingPageExperimentParameters(context.statsigUser, defaultExperimentParameters);

  return {
    notFound: false as const,
    landingPage,
    experimentParameters,
    meta: {
      canonicalUrl: `${ADS_ORIGIN}${pathLocale == null ? "/" : getLocalizedLandingPath(locale)}`,
      description: intl.formatMessage(messages.metaDescription),
      title: intl.formatMessage(messages.metaTitle),
    },
  };
}

export default function LandingRoute() {
  const routeData = useLoaderData<typeof loader>();
  const { user, adAccountId } = useGlobalContext();
  const isEligibleForOrganicCampaignPreview =
    user != null && user.adAccounts.length === 0 && adAccountId == null;
  const isOrganicCampaignPreviewEnabled = useGateValue(CARPET_LITE_ORGANIC_EXPERIENCE_GATE, {
    disableExposureLog: !isEligibleForOrganicCampaignPreview,
  });

  if (routeData.notFound) {
    return <NotFound />;
  }

  if (isEligibleForOrganicCampaignPreview && isOrganicCampaignPreviewEnabled) {
    return <OrganicCampaignLanding />;
  }

  return (
    <LandingPage
      landingPage={routeData.landingPage}
      experimentParameters={routeData.experimentParameters}
    />
  );
}

function getLandingPageExperimentParameters(
  statsigUser: SofaAppLoadContext["statsigUser"],
  defaults: AdsLandingPageExperimentParameters,
): AdsLandingPageExperimentParameters {
  const layer = getStatsigClient().getLayer(statsigUser, LANDING_PAGE_HERO_LAYER);
  const heroAlignmentClass = layer.get("hero_alignment_class", defaults.heroAlignmentClass);

  return {
    heroCtaText: layer.get("hero_cta_text", defaults.heroCtaText).trim() || defaults.heroCtaText,
    heroHeaderText:
      layer.get("hero_header_text", defaults.heroHeaderText).trim() || defaults.heroHeaderText,
    heroSubheaderText:
      layer.get("hero_subheader_text", defaults.heroSubheaderText).trim() ||
      defaults.heroSubheaderText,
    headerCtaText:
      layer.get("header_cta_text", defaults.headerCtaText).trim() || defaults.headerCtaText,
    heroAlignmentClass:
      heroAlignmentClass === "lg:items-center" ? "lg:items-center" : defaults.heroAlignmentClass,
  };
}

function OrganicCampaignLanding() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useGlobalContext();
  const initialWebsiteUrl = new URLSearchParams(location.search).get("website") ?? undefined;

  const continueToOnboarding = (websiteUrl?: string) => {
    const searchParams = new URLSearchParams(location.search);
    if (websiteUrl) {
      searchParams.set("website", websiteUrl);
    } else {
      searchParams.delete("website");
    }

    const query = searchParams.toString();
    const onboardingPath = query ? `/onboarding?${query}` : "/onboarding";
    if (!user) {
      window.location.assign(
        `/auth/login?${new URLSearchParams({ returnTo: onboardingPath }).toString()}`,
      );
      return;
    }

    void navigate(onboardingPath);
  };

  return (
    <CarpetLiteOrganicExperience
      initialWebsiteUrl={initialWebsiteUrl}
      accessToken={user?.accessToken}
      onContinue={continueToOnboarding}
      onSkip={!user ? () => continueToOnboarding() : undefined}
    />
  );
}
